import { Link } from '@tanstack/react-router';
import { CopyIcon, ExternalLinkIcon, RefreshCwIcon } from 'lucide-react';
import { useState } from 'react';

import { LockedCard } from '@/components/feature/LockedCard';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  type AssetSummary,
  type AssetType,
  useAssets,
  useRegenerateAllAssets,
  useRegenerateAsset,
} from '@/lib/hooks/useAssets';
import { cn } from '@/lib/utils';

const ASSET_META: Record<AssetType, { title: string; description: string; requiredTier: 'starter' | 'growth' | 'scale' }> = {
  llms_txt: {
    title: 'llms.txt',
    description: 'Minimal, spec-compliant summary of your API for AI agents to read first.',
    requiredTier: 'starter',
  },
  llms_full_txt: {
    title: 'llms-full.txt',
    description: 'Expanded variant with full endpoint documentation inline.',
    requiredTier: 'starter',
  },
  mcp_server: {
    title: 'MCP server manifest',
    description: 'Model Context Protocol manifest describing your API as discoverable tools.',
    requiredTier: 'growth',
  },
  cursor_rules: {
    title: '.cursorrules',
    description: 'Ruleset for Cursor to use when writing code against your API.',
    requiredTier: 'growth',
  },
  copilot_instructions: {
    title: 'Copilot instructions',
    description: "GitHub Copilot's equivalent of Cursor rules — repo-scoped integration guidance.",
    requiredTier: 'growth',
  },
  agent_skills: {
    title: 'Anthropic Agent Skills',
    description: 'Claude-consumable SKILL.md file describing when and how to call your API.',
    requiredTier: 'starter',
  },
};

const ORDER: AssetType[] = [
  'llms_txt',
  'llms_full_txt',
  'cursor_rules',
  'copilot_instructions',
  'mcp_server',
  'agent_skills',
];

export function AssetsPage() {
  const { data, isLoading } = useAssets();
  const regenerate = useRegenerateAsset();
  const regenerateAll = useRegenerateAllAssets();

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Assets</h1>
          <p className="mt-1 text-sm text-slate-500">
            Files Athena generates from your latest audit. Regenerated automatically on every audit; hosted on your subdomain.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => regenerateAll.mutate()}
          disabled={regenerateAll.isPending || !data}
        >
          <RefreshCwIcon className={cn('h-4 w-4', regenerateAll.isPending && 'animate-spin')} />
          Regenerate all
        </Button>
      </header>

      {data && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Where these are hosted</CardTitle>
            <CardDescription>
              Once DNS + wildcard CNAME is set up, each asset is live at its subdomain URL. For dev, use the path-based URL —
              both resolve the same content.
            </CardDescription>
          </CardHeader>
          <dl className="grid gap-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-slate-500">Subdomain (prod)</dt>
              <dd><code className="font-mono text-xs">{data.hosted_base_url}</code></dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-slate-500">Path (dev / fallback)</dt>
              <dd><code className="font-mono text-xs">{data.dev_base_url}</code></dd>
            </div>
          </dl>
        </Card>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {ORDER.map((type) => {
          const meta = ASSET_META[type];
          const enabled = data?.enabled_types.includes(type) ?? false;
          const summary = data?.items.find((i) => i.type === type);

          if (data && !enabled) {
            return (
              <LockedCard
                key={type}
                title={meta.title}
                description={meta.description}
                requiredTier={meta.requiredTier}
              />
            );
          }

          return (
            <AssetCard
              key={type}
              title={meta.title}
              description={meta.description}
              summary={summary}
              devBaseUrl={data?.dev_base_url ?? ''}
              hostedBaseUrl={data?.hosted_base_url ?? ''}
              onRegenerate={() => regenerate.mutate(type)}
              regenerating={regenerate.isPending && regenerate.variables === type}
              loading={isLoading}
            />
          );
        })}
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Haven't run an audit yet?</CardTitle>
          <CardDescription>
            Assets are generated from the latest complete audit.{' '}
            <Link to="/app/audits" className="underline">Run one</Link> and come back.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

function AssetCard({
  title,
  description,
  summary,
  devBaseUrl,
  hostedBaseUrl,
  onRegenerate,
  regenerating,
  loading,
}: {
  title: string;
  description: string;
  summary: AssetSummary | undefined;
  devBaseUrl: string;
  hostedBaseUrl: string;
  onRegenerate: () => void;
  regenerating: boolean;
  loading: boolean;
}) {
  const [copied, setCopied] = useState<'dev' | 'prod' | null>(null);

  const copy = async (url: string, which: 'dev' | 'prod') => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard denied — fall through silently */
    }
  };

  const suffix = summary?.path ?? '';
  const devUrl = devBaseUrl && suffix ? `${devBaseUrl}${suffix}` : '';
  const prodUrl = hostedBaseUrl && suffix ? `${hostedBaseUrl}${suffix}` : '';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>

      {summary ? (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>v{summary.version}</span>
            <span>·</span>
            <span>{new Date(summary.generated_at).toLocaleString()}</span>
            {summary.generated_from_audit_id && (
              <>
                <span>·</span>
                <Link
                  to="/app/audits/$auditId"
                  params={{ auditId: summary.generated_from_audit_id }}
                  className="underline-offset-4 hover:underline"
                >
                  audit {summary.generated_from_audit_id.slice(0, 8)}
                </Link>
              </>
            )}
          </div>

          <div className="mb-3 space-y-1.5">
            <UrlRow label="Dev" url={devUrl} onCopy={() => copy(devUrl, 'dev')} copied={copied === 'dev'} />
            <UrlRow label="Prod" url={prodUrl} onCopy={() => copy(prodUrl, 'prod')} copied={copied === 'prod'} />
          </div>

          {summary.preview && (
            <pre className="max-h-48 overflow-auto rounded-md bg-slate-900 p-3 text-[11px] leading-snug text-slate-100">
{summary.preview}
{summary.preview.length >= 300 ? '\n…' : ''}
            </pre>
          )}

          <div className="mt-4 flex gap-2">
            <Button size="sm" variant="outline" onClick={onRegenerate} disabled={regenerating}>
              <RefreshCwIcon className={cn('h-3.5 w-3.5', regenerating && 'animate-spin')} />
              {regenerating ? 'Regenerating…' : 'Regenerate'}
            </Button>
            {devUrl && (
              <Button size="sm" variant="ghost" asChild>
                <a href={devUrl} target="_blank" rel="noreferrer">
                  <ExternalLinkIcon className="h-3.5 w-3.5" />
                  Open
                </a>
              </Button>
            )}
          </div>
        </>
      ) : loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div>
          <p className="mb-3 text-sm text-slate-500">Not generated yet.</p>
          <Button size="sm" onClick={onRegenerate} disabled={regenerating}>
            <RefreshCwIcon className={cn('h-3.5 w-3.5', regenerating && 'animate-spin')} />
            {regenerating ? 'Generating…' : 'Generate now'}
          </Button>
        </div>
      )}
    </Card>
  );
}

function UrlRow({
  label,
  url,
  onCopy,
  copied,
}: {
  label: string;
  url: string;
  onCopy: () => void;
  copied: boolean;
}) {
  if (!url) return null;
  return (
    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs dark:border-slate-800 dark:bg-slate-950">
      <span className="w-8 shrink-0 text-slate-500">{label}</span>
      <code className="flex-1 truncate font-mono">{url}</code>
      <button
        type="button"
        onClick={onCopy}
        className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        aria-label={`Copy ${label} URL`}
      >
        <CopyIcon className="h-3.5 w-3.5" />
      </button>
      {copied && <span className="text-[10px] text-[var(--color-success)]">copied</span>}
    </div>
  );
}
